import React, { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';

const SIZES = ['XS*','S','M','L','XL','2XL','3XL'];
const TABS = ['All Staff', 'Personal Info', 'T-Shirts', 'Forms', 'Availability', 'Optional', 'Directory'];
const STATUS_COLORS = {
  complete:    { bg: '#EAF4E2', color: '#2D5016', label: 'Complete' },
  in_progress: { bg: '#FEF6E4', color: '#7a4e00', label: 'In Progress' },
  not_started: { bg: '#F4F4F4', color: '#888',    label: 'Not Started' },
};

const EXPORT_SECTIONS = [
  { key: 'contact',      label: 'Contact Info' },
  { key: 'emergency',    label: 'Emergency Contact' },
  { key: 'medical',      label: 'Medical Info' },
  { key: 'payment',      label: 'Payment / Tax' },
  { key: 'shirts',       label: 'T-Shirt Order' },
  { key: 'forms',        label: 'Forms Status' },
  { key: 'availability', label: 'Availability' },
  { key: 'optional',     label: 'Optional (Muggers, Music)' },
];

const SALT_ROLES = ['Male Counselor', 'Female Counselor', 'Program Staff', 'Day Camp Staff', 'Kitchen', 'Other'];

// Fetch invited emails from database
import { fetchInvitedEmails, clearInvitedEmailsCache } from '../invitedEmails';

const CURRENT_YEAR = '2026';

function formatPhone(val) {
  if (!val) return val;
  const d = String(val).replace(/\D/g, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0,3)})${d.slice(3)}`;
  return `(${d.slice(0,3)})${d.slice(3,6)}-${d.slice(6)}`;
}

function AdminUserMenu({ email, onMyProfile, onPreview, onSignOut }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    function handleClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  const item = (icon, label, onClick, danger) => (
    <button onClick={() => { setOpen(false); onClick(); }}
      style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '10px 16px', fontFamily: "'Nunito', sans-serif", fontSize: '0.88rem', fontWeight: 600, color: danger ? '#c0392b' : '#2D2D2D', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 9 }}
      onMouseEnter={e => e.currentTarget.style.background = '#F7FAF2'}
      onMouseLeave={e => e.currentTarget.style.background = 'none'}>
      <span style={{ fontSize: '1rem', width: 20, textAlign: 'center' }}>{icon}</span>{label}
    </button>
  );
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(v => !v)}
        style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: 8, padding: '6px 12px', fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</span>
        <span style={{ fontSize: '0.65rem', opacity: 0.8 }}>▾</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', background: '#fff', borderRadius: 12, boxShadow: '0 6px 24px rgba(0,0,0,0.14)', minWidth: 210, zIndex: 2000, overflow: 'hidden' }}>
          <div style={{ padding: '10px 16px 8px', fontSize: '0.75rem', color: '#9B9B9B', borderBottom: '1px solid #F0F7E8', fontWeight: 600 }}>{email}</div>
          {item('👤', 'My Profile', onMyProfile)}
          {item('📋', `Summer ${CURRENT_YEAR}`, onPreview)}
          <div style={{ borderTop: '1px solid #F0F7E8', marginTop: 4 }}>{item('🚪', 'Sign out', onSignOut, true)}</div>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard({ session, onSignOut, onPreview, onViewProfile, onMyProfile }) {
  const [submissions, setSubmissions]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [activeTab, setActiveTab]       = useState('All Staff');
  const [selected, setSelected]         = useState(null);
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formFilter, setFormFilter]     = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportSections, setExportSections]   = useState(
    Object.fromEntries(EXPORT_SECTIONS.map(s => [s.key, true]))
  );
  const [directoryRecords, setDirectoryRecords] = useState([]);
  const [dirPublishing, setDirPublishing]       = useState({});
  const [confirmRemove, setConfirmRemove]       = useState({}); // email → true when awaiting confirm
  const [confirmBulkRemove, setConfirmBulkRemove] = useState(false);
  const [showDirModal, setShowDirModal]           = useState(false);
  const [editDirEntry, setEditDirEntry]           = useState(null);
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [confirmDeleteStaff, setConfirmDeleteStaff] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('onboarding_submissions')
      .select('*')
      .order('updated_at', { ascending: false });

    // Fetch invited list with is_admin flag (falls back to email-only if migration hasn't run yet)
    let invitedRaw = [];
    const { data: invitedData, error: invitedError } = await supabase.from('invited_emails').select('email, is_admin');
    if (!invitedError && invitedData) {
      invitedRaw = invitedData;
    } else {
      // Column may not exist yet — fall back to just emails
      const { data: fallback } = await supabase.from('invited_emails').select('email');
      invitedRaw = (fallback || []).map(r => ({ email: r.email, is_admin: false }));
    }
    const yearRoundSet = new Set(invitedRaw.filter(r => r.is_admin).map(r => r.email.toLowerCase()));
    const invitedEmails = invitedRaw.map(r => r.email.toLowerCase());

    const submissionMap = {};
    (data || []).forEach(row => { submissionMap[row.email.toLowerCase()] = row; });

    const merged = invitedEmails.map(email => {
      const lower = email.toLowerCase();
      const row = submissionMap[lower] || { email: lower, id: lower, _notStarted: true };
      return { ...row, _isYearRound: yearRoundSet.has(lower) };
    });

    if (!error) setSubmissions(merged);

    // Also load published directory records
    const { data: dirData } = await supabase.from('staff_directory').select('*');
    setDirectoryRecords(dirData || []);

    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getStatus = (row) => {
    if (!row) return 'not_started';
    if (row.checks?.statusOverride) return row.checks.statusOverride;
    const p = row.personal_info || {};
    const f = row.forms || {};
    const a = row.availability || {};
    const c = row.checks || {};
    const personalComplete = !!(p.firstName && p.lastName && p.phone && p.email && p.address && p.city && p.state && p.zip && p.emergencyName && p.emergencyPhone && p.emergencyRelationship && Object.values(p.shirts || {}).reduce((a,b)=>a+b,0) > 0);
    const formsComplete = !!(f.w4 && f.i9 && f.medical && f.cpr);
    const availComplete = a.ropes !== null && a.ropes !== undefined && a.fancy !== null && a.fancy !== undefined && a.junior !== null && a.junior !== undefined;
    const submitComplete = !!(c.accuracy && c.policies && c.texts && c.liability);
    if (personalComplete && formsComplete && availComplete && submitComplete) return 'complete';
    if (p.firstName || f.w4 || a.ropes !== undefined) return 'in_progress';
    return 'not_started';
  };

  const updateStatusOverride = async (row, value) => {
    if (!row.id) return;
    const newChecks = { ...(row.checks || {}) };
    if (value) { newChecks.statusOverride = value; } else { delete newChecks.statusOverride; }
    await supabase.from('onboarding_submissions').update({ checks: newChecks }).eq('id', row.id);
    setSubmissions(prev => prev.map(r => r.id === row.id ? { ...r, checks: newChecks } : r));
    if (selected?.id === row.id) setSelected(s => ({ ...s, checks: newChecks }));
  };

  const filtered = submissions.filter(row => {
    const p = row.personal_info || {};
    const f = row.forms || {};
    const name = `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase();
    const email = (row.email || '').toLowerCase();
    const matchesSearch = name.includes(search.toLowerCase()) || email.includes(search.toLowerCase());
    const status = getStatus(row);
    const matchesStatus = statusFilter === 'all' || status === statusFilter;
    const matchesForm = formFilter === 'all' ||
      (formFilter === 'missing_w4'       && !f.w4) ||
      (formFilter === 'missing_i9'       && !f.i9) ||
      (formFilter === 'missing_i9_docs'  && !f.i9_listA && !(f.i9_listB && f.i9_listC)) ||
      (formFilter === 'missing_medical'  && !f.medical) ||
      (formFilter === 'missing_cpr'      && !f.cpr) ||
      (formFilter === 'missing_deposit'  && !f.directdeposit);
    return matchesSearch && matchesStatus && matchesForm;
  });

  const sorted = [...filtered].sort((a, b) => {
    const ap = a.personal_info || {};
    const bp = b.personal_info || {};
    if (sortBy === 'name') {
      const aName = `${ap.firstName||''} ${ap.lastName||''}`.trim().toLowerCase();
      const bName = `${bp.firstName||''} ${bp.lastName||''}`.trim().toLowerCase();
      if (!aName) return 1;
      if (!bName) return -1;
      return aName.localeCompare(bName);
    }
    if (sortBy === 'complete_first') {
      const order = { complete: 0, in_progress: 1, not_started: 2 };
      return order[getStatus(a)] - order[getStatus(b)];
    }
    if (sortBy === 'not_started_first') {
      const order = { not_started: 0, in_progress: 1, complete: 2 };
      return order[getStatus(a)] - order[getStatus(b)];
    }
    if (sortBy === 'shirts') {
      const aShirts = Object.values(ap.shirts || {}).reduce((x,y) => x+y, 0);
      const bShirts = Object.values(bp.shirts || {}).reduce((x,y) => x+y, 0);
      return (bShirts > 0 ? 1 : 0) - (aShirts > 0 ? 1 : 0);
    }
    return 0;
  });

  // T-shirt totals across all submissions
  const shirtTotals = SIZES.reduce((acc, size) => {
    acc[size] = submissions.reduce((sum, row) => sum + (row.personal_info?.shirts?.[size] || 0), 0);
    return acc;
  }, {});
  const grandTotal = Object.values(shirtTotals).reduce((a, b) => a + b, 0);

  const toggleSelectEmail = (email) => {
    setSelectedEmails(prev => {
      const next = new Set(prev);
      next.has(email) ? next.delete(email) : next.add(email);
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelectedEmails(new Set(filtered.map(r => r.email)));
  };

  const clearSelection = () => setSelectedEmails(new Set());

  const emailSelected = () => {
    const emails = [...selectedEmails].join(', ');
    navigator.clipboard.writeText(emails).then(() => {
     alert(`${selectedEmails.size} email addresses copied to clipboard! Paste them into the BCC field of your email.`);
   });
  };

  const updateRole = async (rowId, value) => {
    if (!rowId) return;
    await supabase
      .from('onboarding_submissions')
      .update({ salt_role: value || null })
      .eq('id', rowId);
    setSubmissions(prev => prev.map(r => r.id === rowId ? { ...r, salt_role: value } : r));
  };

  const toDirectoryCategory = (role) => {
    if (role === 'Male Counselor' || role === 'Female Counselor') return 'Counselor';
    return role || 'Other';
  };

  const publishStaff = async (row) => {
    setDirPublishing(prev => ({ ...prev, [row.email]: true }));
    const p = row.personal_info || {};
    const dp = row.directory_profile || {};
    const name = `${p.firstName || ''} ${p.lastName || ''}`.trim() || row.email;
    const { error } = await supabase.from('staff_directory').upsert({
      email: row.email,
      name,
      role:       dp.positionTitle || '',
      category:   toDirectoryCategory(row.salt_role),
      bio:        dp.bio           || '',
      hometown:   dp.hometown      || '',
      salt_years: dp.saltStints?.length
        ? dp.saltStints.reduce((total, s) => {
            const to = s.to === 'present' ? new Date().getFullYear() : parseInt(s.to);
            return total + Math.max(0, to - parseInt(s.from) + 1);
          }, 0)
        : dp.saltYears ? parseInt(dp.saltYears) : null,
      photo_url:  dp.photoUrl      || null,
      visible:    true,
    }, { onConflict: 'email' });
    if (error) {
      alert('Publish failed: ' + error.message);
    } else {
      const { data: dirData } = await supabase.from('staff_directory').select('*');
      setDirectoryRecords(dirData || []);
    }
    setDirPublishing(prev => ({ ...prev, [row.email]: false }));
  };

  const unpublishStaff = async (email) => {
    setDirPublishing(prev => ({ ...prev, [email]: true }));
    const { error } = await supabase.from('staff_directory').delete().eq('email', email);
    if (!error) {
      setDirectoryRecords(prev => prev.filter(r => r.email !== email));
    }
    setDirPublishing(prev => ({ ...prev, [email]: false }));
    setConfirmRemove(prev => ({ ...prev, [email]: false }));
  };

  const saveDirectoryEntry = async (entry) => {
    const { error } = await supabase.from('staff_directory').upsert(entry, { onConflict: 'email' });
    if (error) { alert('Save failed: ' + error.message); return false; }
    const { data: dirData } = await supabase.from('staff_directory').select('*');
    setDirectoryRecords(dirData || []);
    setShowDirModal(false);
    setEditDirEntry(null);
    return true;
  };

  const addStaff = async ({ email, firstName, lastName, isYearRound }) => {
    const lower = email.toLowerCase().trim();
    if (submissions.find(r => r.email === lower)) { alert('A staff member with this email already exists.'); return false; }

    // Send Supabase auth invite (creates auth account + emails the user)
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/invite-user`;
    let fnRes, fnData;
    try {
      fnRes = await fetch(fnUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentSession?.access_token}`,
        },
        body: JSON.stringify({ email: lower, redirectTo: window.location.origin }),
      });
      fnData = await fnRes.json();
    } catch (err) {
      alert('Invite failed: ' + err.message);
      return false;
    }
    if (!fnRes.ok) { alert('Invite failed: ' + (fnData.error || 'Unknown error')); return false; }

    // Upsert to invited_emails (sets admin flag if year round)
    await supabase.from('invited_emails').upsert({ email: lower, is_admin: !!isYearRound }, { onConflict: 'email' });
    clearInvitedEmailsCache();

    // Create the submission record
    const checks = isYearRound ? { statusOverride: 'complete', isYearRound: true } : undefined;
    const { error } = await supabase.from('onboarding_submissions').insert({
      email: lower,
      personal_info: { firstName: firstName.trim(), lastName: lastName.trim(), email: lower },
      ...(checks ? { checks } : {}),
    });
    if (error) { alert('Add failed: ' + error.message); return false; }
    await fetchData();
    setShowAddStaffModal(false);
    return true;
  };

  const deleteStaff = async (email) => {
    await supabase.from('staff_directory').delete().eq('email', email);
    await supabase.from('invited_emails').delete().eq('email', email);
    const { error } = await supabase.from('onboarding_submissions').delete().eq('email', email);
    if (error) { alert('Delete failed: ' + error.message); return; }
    clearInvitedEmailsCache();
    setSubmissions(prev => prev.filter(r => r.email !== email));
    setDirectoryRecords(prev => prev.filter(r => r.email !== email));
    setConfirmDeleteStaff(null);
    if (selected?.email === email) setSelected(null);
  };

  const runExport = () => {
    const sel = exportSections;
    const headers = [
      'Email', 'Status',
      ...(sel.contact    ? ['First Name','Last Name','Phone','DOB','Address','City','State','ZIP'] : []),
      ...(sel.emergency  ? ['Emergency Contact','Emergency Relationship','Emergency Phone'] : []),
      ...(sel.medical    ? ['Allergies','Medications','Medical Notes'] : []),
      ...(sel.payment    ? ['SSN','Bank','Routing','Account'] : []),
      ...(sel.shirts     ? [...SIZES.map(sz => `Shirts ${sz}`), 'Shirt Extra Cost'] : []),
      ...(sel.forms      ? ['W-4','I-9','Medical Form','CPR/First Aid','Lifeguard','BG Check'] : []),
      ...(sel.availability ? ['Ropes Training','Ropes Level','Fancy Schmancy','Fancy Roles','Junior Staff','Junior Dates Unavailable'] : []),
      ...(sel.optional   ? ['Muggers','Music Interest','Music Instruments','Music Dates Unavailable'] : []),
    ];

    const rows = submissions.map(row => {
      const p = row.personal_info || {};
      const f = row.forms || {};
      const a = row.availability || {};
      const o = row.optional_info || {};
      const shirtTotal = Object.values(p.shirts || {}).reduce((a,b) => a+b, 0);
      const extraCost  = Math.max(0, shirtTotal - 1) * 8;
      return [
        row.email,
        getStatus(row),
        ...(sel.contact    ? [p.firstName, p.lastName, p.phone, p.dob, p.address, p.city, p.state, p.zip] : []),
        ...(sel.emergency  ? [p.emergencyName, p.emergencyRelationship, p.emergencyPhone] : []),
        ...(sel.medical    ? [p.allergies, p.medications, p.medicalNotes] : []),
        ...(sel.payment    ? [p.ssn, p.bankName, p.routing, p.accountNumber] : []),
        ...(sel.shirts     ? [...SIZES.map(sz => p.shirts?.[sz] || 0), `$${extraCost}`] : []),
        ...(sel.forms      ? [f.w4?'Yes':'No', f.i9?'Yes':'No', f.medical?'Yes':'No', f.cpr?'Yes':'No', f.lifeguard?'Yes':'No', f.bgCheckDone?'Yes':'No'] : []),
        ...(sel.availability ? [a.ropes?'Yes':'No', `${a.ropesLevel||''} ${a.ropesOtherDetail||''}`.trim(), a.fancy?'Yes':'No', (a.fancyRoles||[]).join('; '), a.junior||'', a.juniorDates||''] : []),
        ...(sel.optional   ? [o.muggers||'', o.musicInterest||'', o.musicInstruments||'', o.musicDatesUnavailable||''] : []),
      ].map(v => `"${String(v ?? '').replace(/"/g, '""')}"`);
    });

    const csv = [headers.map(h=>`"${h}"`).join(','), ...rows.map(r=>r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'camp_phillip_staff.csv';
    a.click(); URL.revokeObjectURL(url);
    setShowExportModal(false);
  };

  return (
    <div style={s.wrap}>
      <style>{`
        .dir-card { display: flex; align-items: center; gap: 14px; }
        .dir-btn-group { display: flex; gap: 8px; flex-shrink: 0; flex-wrap: nowrap; align-items: center; }
        .dir-sec-hdr-row { display: flex; align-items: center; gap: 10px; background: #F4F8EE; border: 1px solid #C8D9B0; border-radius: 8px; padding: 10px 14px; margin-bottom: 16px; }
        .dir-sec-hdr { font-size: 0.88rem; font-weight: 800; color: #2D5016; margin: 0; }
        @media (max-width: 600px) {
          .dir-card { flex-wrap: wrap; }
          .dir-btn-group { flex-wrap: wrap; flex-shrink: 1; justify-content: flex-end; }
        }
      `}</style>
      {/* Header */}
      <div style={s.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <img src="/Camp_Phillip_Logo.png" alt="Camp Phillip" style={{ height: 52, filter: 'brightness(0) invert(1)' }} />
          <div>
            <div style={s.badge}>Admin Dashboard</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button style={{ ...s.exportBtn, background: 'rgba(255,255,255,0.15)', color: '#fff' }} onClick={onPreview}>👁 Preview Form</button>
          <button style={s.exportBtn} onClick={() => setShowExportModal(true)}>⬇ Export CSV</button>
          <AdminUserMenu email={session?.user?.email} onMyProfile={onMyProfile} onPreview={onPreview} onSignOut={onSignOut} />
        </div>
      </div>

      {/* Stats */}
      <div style={s.statsRow}>
        {[
          { label: 'Total invited', value: submissions.length },
          { label: 'Complete', value: submissions.filter(r => getStatus(r) === 'complete').length },
          { label: 'In progress', value: submissions.filter(r => getStatus(r) === 'in_progress').length },
          { label: 'Not started', value: submissions.filter(r => getStatus(r) === 'not_started').length },
        ].map(stat => (
          <div key={stat.label} style={s.statCard}>
            <div style={s.statNum}>{stat.value}</div>
            <div style={s.statLabel}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={s.tabRow}>
        {TABS.map(t => {
          const publishedEmails = new Set(directoryRecords.map(r => r.email?.toLowerCase()));
          const pendingCount = t === 'Directory'
            ? submissions.filter(r => r.directory_profile?.submittedForDirectory && !publishedEmails.has(r.email?.toLowerCase())).length
            : 0;
          return (
            <button key={t} style={{ ...s.tabBtn, ...(activeTab === t ? s.tabActive : {}), position: 'relative' }}
              onClick={() => setActiveTab(t)}>
              {t}
              {!loading && pendingCount > 0 && (
                <span style={{ position: 'absolute', top: -6, right: -6, background: '#e74c3c', color: '#fff', borderRadius: '50%', width: 18, height: 18, fontSize: '0.65rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                  {pendingCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      {activeTab !== 'T-Shirts' && activeTab !== 'Directory' && (
        <div style={{ padding: '0 32px 12px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input placeholder="Search by name or email…" value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...s.search, margin: 0, width: 220, flex: 'none' }} />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={s.filterSelect}>
            <option value="all">All statuses</option>
            <option value="complete">Complete</option>
            <option value="in_progress">In Progress</option>
            <option value="not_started">Not Started</option>
          </select>
          <select value={formFilter} onChange={e => setFormFilter(e.target.value)} style={s.filterSelect}>
            <option value="all">All forms</option>
            <option value="missing_w4">Missing W-4</option>
            <option value="missing_i9">Missing I-9</option>
            <option value="missing_i9_docs">Missing I-9 Supporting Docs</option>
            <option value="missing_medical">Missing Health Form</option>
            <option value="missing_cpr">Missing CPR Cert</option>
            <option value="missing_deposit">Missing Direct Deposit</option>
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={s.filterSelect}>
            <option value="name">Sort: Name (A-Z)</option>
            <option value="complete_first">Sort: Complete first</option>
            <option value="not_started_first">Sort: Not Started first</option>
           <option value="shirts">Sort: Shirt orders first</option>
          </select>
          {(statusFilter !== 'all' || formFilter !== 'all' || search) && (
            <button onClick={() => { setStatusFilter('all'); setFormFilter('all'); setSearch(''); }}
              style={{ padding: '8px 12px', background: '#F4F8EE', border: '1px solid #C8D9B0', borderRadius: 7, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: '#C0392B', fontFamily: "'Nunito', sans-serif" }}>
              ✕ Clear filters
            </button>
          )}
          <button onClick={() => setShowAddStaffModal(true)}
            style={{ marginLeft: 'auto', padding: '8px 16px', background: '#2D5016', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700, fontFamily: "'Nunito', sans-serif" }}>
            + Add staff
          </button>
        </div>
      )}

      {/* Email toolbar */}
      {activeTab !== 'T-Shirts' && selectedEmails.size > 0 && (
        <div style={{ margin: '0 32px 12px', padding: '12px 16px', background: '#EAF4E2', border: '1px solid #7BA05B', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#2D5016' }}>{selectedEmails.size} selected</span>
          <button onClick={emailSelected} style={{ padding: '7px 16px', background: '#2D5016', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', fontFamily: "'Nunito', sans-serif" }}>
            📋 Copy emails to clipboard
          </button>
          <button onClick={selectAllFiltered} style={{ padding: '7px 16px', background: '#fff', border: '1px solid #C8D9B0', borderRadius: 7, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', fontFamily: "'Nunito', sans-serif", color: '#2D5016' }}>
            Select all {filtered.length}
          </button>
          <button onClick={clearSelection} style={{ padding: '7px 12px', background: 'none', border: 'none', cursor: 'pointer', color: '#C0392B', fontWeight: 600, fontSize: '0.85rem', fontFamily: "'Nunito', sans-serif" }}>
            Clear
          </button>
        </div>
      )}

      {/* T-Shirt Totals Panel */}
      {activeTab === 'T-Shirts' && (
        <div style={{ padding: '0 32px 24px' }}>
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #C8D9B0', overflow: 'hidden', boxShadow: '0 1px 8px rgba(45,80,22,0.07)' }}>
            <div style={{ padding: '16px 20px', background: '#F4F8EE', borderBottom: '2px solid #C8D9B0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{ color: '#2D5016', fontSize: '1rem' }}>🎽 T-Shirt Order Totals</strong>
              <span style={{ fontSize: '0.85rem', color: '#6B7F5A' }}>{grandTotal} total shirts across {submissions.filter(s => Object.values(s.personal_info?.shirts || {}).reduce((a,b)=>a+b,0) > 0).length} staff</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 1, background: '#C8D9B0' }}>
              {SIZES.map(size => (
                <div key={size} style={{ background: '#fff', padding: '20px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#2D5016' }}>{shirtTotals[size]}</div>
                  <div style={{ fontSize: '0.85rem', color: '#6B7F5A', marginTop: 4 }}>Size {size}</div>
                </div>
              ))}
            </div>
            <div style={{ padding: '14px 20px', background: '#2D5016', color: '#fff', display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
              <span>Total shirts: {grandTotal}</span>
              <span>Extra shirt revenue: ${Math.max(0, grandTotal - submissions.filter(s => Object.values(s.personal_info?.shirts||{}).reduce((a,b)=>a+b,0) > 0).length) * 8}</span>
            </div>
          </div>

          {/* Per-person breakdown */}
          <div style={{ marginTop: 20, background: '#fff', borderRadius: 12, border: '1px solid #C8D9B0', overflow: 'hidden', boxShadow: '0 1px 8px rgba(45,80,22,0.07)' }}>
            <div style={{ padding: '14px 20px', background: '#F4F8EE', borderBottom: '2px solid #C8D9B0' }}>
              <strong style={{ color: '#2D5016' }}>Per-Person Breakdown</strong>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr>
                  <th style={s.th}>Name</th>
                  {SIZES.map(sz => <th key={sz} style={s.th}>{sz}</th>)}
                  <th style={s.th}>Total</th>
                  <th style={s.th}>Extra Cost</th>
                </tr>
              </thead>
              <tbody>
                {submissions.filter(row => Object.values(row.personal_info?.shirts || {}).reduce((a,b)=>a+b,0) > 0).map(row => {
                  const p = row.personal_info || {};
                  const total = SIZES.reduce((a, sz) => a + (p.shirts?.[sz] || 0), 0);
                  const extra = Math.max(0, total - 1) * 8;
                  return (
                    <tr key={row.id} style={s.tr}>
                      <td style={s.td}>
                        <strong>{p.firstName} {p.lastName}</strong>
                        <span style={{ display: 'block', fontSize: '0.76rem', color: '#6B7F5A' }}>{row.email}</span>
                      </td>
                      {SIZES.map(sz => (
                        <td key={sz} style={{ ...s.td, textAlign: 'center' }}>{p.shirts?.[sz] || '—'}</td>
                      ))}
                      <td style={{ ...s.td, fontWeight: 700 }}>{total}</td>
                      <td style={s.td}>${extra}</td>
                    </tr>
                  );
                })}
                {/* Totals row */}
                <tr style={{ background: '#F4F8EE', fontWeight: 700 }}>
                  <td style={{ ...s.td, fontWeight: 700 }}>TOTALS</td>
                  {SIZES.map(sz => <td key={sz} style={{ ...s.td, textAlign: 'center', fontWeight: 700, color: '#2D5016' }}>{shirtTotals[sz] || '—'}</td>)}
                  <td style={{ ...s.td, fontWeight: 700, color: '#2D5016' }}>{grandTotal}</td>
                  <td style={{ ...s.td, fontWeight: 700, color: '#2D5016' }}>${Math.max(0, grandTotal - submissions.filter(s => Object.values(s.personal_info?.shirts||{}).reduce((a,b)=>a+b,0) > 0).length) * 8}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Main table */}
      {activeTab !== 'T-Shirts' && activeTab !== 'Directory' && (
        loading ? (
          <p style={{ color: '#6B7F5A', padding: 24 }}>Loading…</p>
        ) : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>
                    <input type="checkbox"
                      checked={filtered.length > 0 && filtered.every(r => selectedEmails.has(r.email))}
                      onChange={e => e.target.checked ? selectAllFiltered() : clearSelection()}
                      style={{ accentColor: '#2D5016', width: 15, height: 15, cursor: 'pointer' }}/>
                  </th>
                  <th style={s.th}>Name / Email</th>
                  <th style={s.th}>Status</th>
                  {activeTab === 'All Staff' && <th style={s.th}>Role</th>}
                  {activeTab === 'Personal Info' && <><th style={s.th}>Phone</th><th style={s.th}>Shirts</th></>}
                  {activeTab === 'Forms' && <><th style={s.th}>W-4</th><th style={s.th}>I-9</th><th style={s.th}>Medical</th><th style={s.th}>CPR</th><th style={s.th}>Lifeguard</th><th style={s.th}>BG Check</th></>}
                  {activeTab === 'Availability' && <><th style={s.th}>Ropes</th><th style={s.th}>Fancy Schmancy</th><th style={s.th}>Junior Staff</th></>}
                  {activeTab === 'Optional' && <><th style={s.th}>Muggers</th><th style={s.th}>Music</th></>}
                  <th style={s.th}>Last updated</th>
                  <th style={s.th}></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(row => {
                  const status = getStatus(row);
                  const sc = STATUS_COLORS[status];
                  const p = row.personal_info || {};
                  const f = row.forms || {};
                  const a = row.availability || {};
                  const o = row.optional_info || {};
                  const shirts = Object.entries(p.shirts || {}).filter(([,v]) => v > 0).map(([k,v]) => `${k}×${v}`).join(', ');
                  return (
                    <tr key={row.id || row.email} style={{ ...s.tr, background: selectedEmails.has(row.email) ? '#EAF4E2' : '' }}>
                      <td style={s.td}>
                        <input type="checkbox" checked={selectedEmails.has(row.email)}
                          onChange={() => toggleSelectEmail(row.email)}
                          style={{ accentColor: '#2D5016', width: 15, height: 15, cursor: 'pointer' }}/>
                      </td>
                      <td style={s.td}>
                        <strong style={{ display: 'block', fontSize: '0.9rem' }}>
                          {p.firstName || p.lastName ? `${p.firstName||''} ${p.lastName||''}`.trim() : '—'}
                        </strong>
                        <span style={{ fontSize: '0.78rem', color: '#6B7F5A' }}>{row.email}</span>
                      </td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span style={{ ...s.pill, background: sc.bg, color: sc.color }}>{sc.label}</span>
                          {row._isYearRound && (
                            <span style={{ ...s.pill, background: '#FEF3C7', color: '#92400E', fontSize: '0.72rem' }}>⭐ Year Round</span>
                          )}
                        </div>
                      </td>
                      {activeTab === 'All Staff' && (
                        <td style={s.td} onClick={e => e.stopPropagation()}>
                          <select
                            value={row.salt_role || ''}
                            onChange={e => updateRole(row.id, e.target.value)}
                            disabled={!row.id || row._notStarted}
                            style={{ fontSize: '0.82rem', padding: '4px 8px', borderRadius: 6, border: '1.5px solid #C8D9B0', background: row.salt_role ? '#EAF4E2' : '#F4F8EE', color: '#2D5016', fontFamily: "'Nunito', sans-serif", fontWeight: 600, cursor: 'pointer', minWidth: 120 }}
                          >
                            <option value="">— Assign role —</option>
                            {SALT_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </td>
                      )}
                      {activeTab === 'Personal Info' && (
                        <><td style={s.td}>{formatPhone(p.phone)||'—'}</td><td style={s.td}>{shirts||'—'}</td></>
                      )}
                      {activeTab === 'Forms' && (
                        <>{['w4','i9','medical','cpr'].map(k => (
                          <td key={k} style={s.td}>{f[k] ? '✅' : '—'}</td>
                        ))}
                        <td style={s.td}>{f.lifeguard ? '✅' : '—'}</td>
                        <td style={s.td}>{f.bgCheckDone ? '✅' : '—'}</td></>
                      )}
                      {activeTab === 'Availability' && (
                        <><td style={s.td}>{a.ropes===true ? `Yes (${a.ropesLevel||''})` : a.ropes===false ? 'No' : '—'}</td>
                        <td style={s.td}>{a.fancy===true ? 'Yes' : a.fancy===false ? 'No' : '—'}</td>
                        <td style={s.td}>{a.junior||'—'}</td></>
                      )}
                      {activeTab === 'Optional' && (
                        <><td style={s.td}>{o.muggers==='yes' ? '✅ Joined' : o.muggers==='no' ? 'No' : '—'}</td>
                        <td style={s.td}>{o.musicInterest==='yes' ? `✅ ${o.musicInstruments||''}` : o.musicInterest==='no' ? 'No' : '—'}</td></>
                      )}
                      <td style={{ ...s.td, fontSize: '0.78rem', color: '#6B7F5A' }}>
                        {row.updated_at ? new Date(row.updated_at).toLocaleDateString() : '—'}
                      </td>
                      <td style={s.td}>
                        {onViewProfile && !row._notStarted && <button style={{ ...s.viewBtn, marginRight: 6 }} onClick={() => onViewProfile(row)}>Profile</button>}
                        <button style={s.viewBtn} onClick={() => setSelected(row)}>View</button>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={10} style={{ ...s.td, textAlign: 'center', color: '#6B7F5A', padding: 32 }}>No results</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Directory tab */}
      {activeTab === 'Directory' && (() => {
        const publishedEmails = new Set(directoryRecords.map(r => r.email?.toLowerCase()));
        const pending      = submissions.filter(r => r.directory_profile?.submittedForDirectory && !publishedEmails.has(r.email?.toLowerCase()));
        const published    = submissions.filter(r => publishedEmails.has(r.email?.toLowerCase()));
        const notPublished = submissions.filter(r => !r.directory_profile?.submittedForDirectory && !publishedEmails.has(r.email?.toLowerCase()));
        const publishedIncomplete = published.filter(r => !r.directory_profile?.submittedForDirectory || !r.personal_info?.firstName);
        const publishedComplete   = published.filter(r =>  r.directory_profile?.submittedForDirectory &&  r.personal_info?.firstName);

        const removeAllIncomplete = async () => {
          for (const row of publishedIncomplete) {
            await supabase.from('staff_directory').delete().eq('email', row.email);
          }
          const { data: dirData } = await supabase.from('staff_directory').select('*');
          setDirectoryRecords(dirData || []);
          setConfirmBulkRemove(false);
        };

        // section: 'pending' | 'published' | 'none'
        const dirCard = (row, section) => {
          const p   = row.personal_info || {};
          const dp  = row.directory_profile || {};
          const name = p.firstName ? `${p.firstName} ${p.lastName || ''}`.trim() : row.email;
          const initials = name.split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2);
          const busy = dirPublishing[row.email];
          const canPublish = dp.submittedForDirectory && dp.positionTitle;
          const isPublishedIncomplete = section === 'published' && (!dp.submittedForDirectory || !p.firstName);
          const bgColor   = section === 'pending' ? '#FFFBF0' : isPublishedIncomplete ? '#FFF8F0' : section === 'published' ? '#F7FBF2' : '#FAFAFA';
          const bdrColor  = section === 'pending' ? '#FAC775' : isPublishedIncomplete ? '#F5C4A0' : section === 'published' ? '#B8D98A' : '#E0E0E0';
          return (
            <div key={row.email} className="dir-card" style={{ padding: '14px 16px', background: bgColor, border: `1px solid ${bdrColor}`, borderRadius: 12, marginBottom: 10 }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: section === 'none' ? '#888' : '#2D5016', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '1.1rem', fontWeight: 800 }}>
                {dp.photoUrl ? <img src={dp.photoUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }} /> : initials}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 800, fontSize: '0.95rem', color: section === 'none' ? '#555' : '#1a2e0a' }}>{name}</span>
                  {isPublishedIncomplete && (
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, background: '#FEE4C8', color: '#8B4513', borderRadius: 20, padding: '2px 8px', border: '1px solid #F5C4A0' }}>
                      ⚠️ Profile not submitted
                    </span>
                  )}
                </div>
                {dp.positionTitle
                  ? <div style={{ fontSize: '0.82rem', color: section === 'none' ? '#999' : '#5A7A3A', fontWeight: 600 }}>{dp.positionTitle}</div>
                  : <div style={{ fontSize: '0.78rem', color: '#aaa', fontStyle: 'italic' }}>No info entered yet</div>
                }
                {dp.bio && <div style={{ fontSize: '0.78rem', color: '#6B7F5A', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 340 }}>{dp.bio}</div>}
              </div>
              <div className="dir-btn-group">
                {onViewProfile && <button style={s.viewBtn} onClick={() => onViewProfile(row)}>Edit profile</button>}
                {section === 'published' && (() => {
                  const dr = directoryRecords.find(r => r.email?.toLowerCase() === row.email?.toLowerCase());
                  return dr ? (
                    <button style={s.viewBtn} onClick={() => { setEditDirEntry(dr); setShowDirModal(true); }}>Edit entry</button>
                  ) : null;
                })()}
                {section === 'pending' && (
                  <button disabled={busy || !canPublish} onClick={() => publishStaff(row)}
                    title={!dp.submittedForDirectory ? 'Staff must submit their profile first' : !dp.positionTitle ? 'Position title required' : ''}
                    style={{ padding: '6px 14px', background: canPublish ? '#2E7DAF' : '#C8D9B0', color: '#fff', border: 'none', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: '0.8rem', cursor: canPublish ? 'pointer' : 'not-allowed' }}>
                    {busy ? 'Publishing…' : '🌐 Publish'}
                  </button>
                )}
                {section === 'published' && (
                  <>
                    <button disabled={busy} onClick={() => publishStaff(row)}
                      style={{ padding: '6px 14px', background: 'transparent', color: '#2D5016', border: '1.5px solid #7BA05B', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                      {busy ? '…' : '↻ Update'}
                    </button>
                    {confirmRemove[row.email] ? (
                      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                        <span style={{ fontSize: '0.75rem', color: '#a32d2d', fontWeight: 700 }}>Sure?</span>
                        <button disabled={busy} onClick={() => unpublishStaff(row.email)}
                          style={{ padding: '6px 12px', background: '#a32d2d', color: '#fff', border: 'none', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                          Yes, remove
                        </button>
                        <button onClick={() => setConfirmRemove(prev => ({ ...prev, [row.email]: false }))}
                          style={{ padding: '6px 10px', background: 'transparent', color: '#555', border: '1.5px solid #ccc', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button disabled={busy} onClick={() => setConfirmRemove(prev => ({ ...prev, [row.email]: true }))}
                        style={{ padding: '6px 14px', background: 'transparent', color: '#a32d2d', border: '1.5px solid #f7c1c1', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                        Remove
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        };
        return (
          <div style={{ padding: '24px 32px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
              <button onClick={() => { setEditDirEntry(null); setShowDirModal(true); }}
                style={{ padding: '9px 18px', background: '#2D5016', color: '#fff', border: 'none', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' }}>
                + Add staff manually
              </button>
            </div>
            <div style={{ marginBottom: 28 }}>
              <div className="dir-sec-hdr-row">
                <h3 className="dir-sec-hdr">Pending Approval</h3>
                <span style={{ background: pending.length > 0 ? '#e74c3c' : '#C8D9B0', color: pending.length > 0 ? '#fff' : '#6B7F5A', borderRadius: 20, padding: '2px 8px', fontSize: '0.78rem', fontWeight: 700, flexShrink: 0 }}>{pending.length}</span>
              </div>
              {pending.length === 0
                ? <p style={{ color: '#9B9B9B', fontSize: '0.88rem', fontStyle: 'italic' }}>No profiles waiting for approval.</p>
                : pending.map(row => dirCard(row, 'pending'))}
            </div>

            {/* Published */}
            <div style={{ marginBottom: 28 }}>
              <div className="dir-sec-hdr-row">
                <h3 className="dir-sec-hdr">Published on campphillip.com</h3>
                <span style={{ background: '#EAF4E2', color: '#2D5016', border: '1px solid #C0DD97', borderRadius: 20, padding: '2px 8px', fontSize: '0.78rem', fontWeight: 700, flexShrink: 0 }}>{published.length}</span>
                {publishedIncomplete.length > 0 && (
                  confirmBulkRemove ? (
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ fontSize: '0.75rem', color: '#a32d2d', fontWeight: 700 }}>Remove all {publishedIncomplete.length}?</span>
                      <button onClick={removeAllIncomplete}
                        style={{ padding: '5px 12px', background: '#a32d2d', color: '#fff', border: 'none', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
                        Yes, remove
                      </button>
                      <button onClick={() => setConfirmBulkRemove(false)}
                        style={{ padding: '5px 10px', background: 'transparent', color: '#555', border: '1.5px solid #ccc', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmBulkRemove(true)}
                      style={{ marginLeft: 'auto', padding: '5px 13px', background: 'transparent', color: '#a32d2d', border: '1.5px solid #f7c1c1', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
                      Remove {publishedIncomplete.length} incomplete
                    </button>
                  )
                )}
              </div>
              {published.length === 0
                ? <p style={{ color: '#9B9B9B', fontSize: '0.88rem', fontStyle: 'italic' }}>No one published yet.</p>
                : (
                  <>
                    {publishedIncomplete.map(row => dirCard(row, 'published'))}
                    {publishedComplete.map(row => dirCard(row, 'published'))}
                  </>
                )}
            </div>

            {/* Not Published */}
            <div>
              <div className="dir-sec-hdr-row">
                <h3 className="dir-sec-hdr">Not Published</h3>
                <span style={{ background: '#F0F0F0', color: '#777', borderRadius: 20, padding: '2px 8px', fontSize: '0.78rem', fontWeight: 700, flexShrink: 0 }}>{notPublished.length}</span>
              </div>
              {notPublished.length === 0
                ? <p style={{ color: '#9B9B9B', fontSize: '0.88rem', fontStyle: 'italic' }}>Everyone has been published.</p>
                : notPublished.map(row => dirCard(row, 'none'))}
            </div>
          </div>
        );
      })()}

      {/* Detail modal */}
      {selected && <DetailModal
        row={selected}
        dirRecord={directoryRecords.find(r => r.email === selected.email) || null}
        onClose={() => setSelected(null)}
        onRoleChange={(val) => updateRole(selected.id, val)}
        onStatusOverride={(val) => updateStatusOverride(selected, val)}
        onDelete={!selected._notStarted ? () => setConfirmDeleteStaff(selected.email) : null}
        onPhotoUpdate={(url) => setDirectoryRecords(prev => prev.map(r => r.email === selected.email ? { ...r, photo_url: url } : r))}
        onUpdate={(updates) => {
          setSubmissions(prev => prev.map(r => r.email === selected.email ? { ...r, ...updates } : r));
          setSelected(s => ({ ...s, ...updates }));
        }}
      />}

      {/* Export modal */}
      {showExportModal && (
        <div style={m.overlay} onClick={() => setShowExportModal(false)}>
          <div style={{ ...m.modal, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div style={m.modalHeader}>
              <h2 style={m.modalTitle}>Customize Export</h2>
              <button style={m.closeBtn} onClick={() => setShowExportModal(false)}>✕</button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <p style={{ fontSize: '0.88rem', color: '#6B7F5A', marginBottom: 16 }}>Choose which sections to include in your CSV:</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                {EXPORT_SECTIONS.map(sec => (
                  <label key={sec.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: exportSections[sec.key] ? '#EAF4E2' : '#F4F8EE', border: `2px solid ${exportSections[sec.key] ? '#7BA05B' : '#C8D9B0'}`, borderRadius: 8, cursor: 'pointer', fontSize: '0.92rem', fontWeight: 600 }}>
                    <input type="checkbox" checked={exportSections[sec.key]} onChange={e => setExportSections(prev => ({ ...prev, [sec.key]: e.target.checked }))}
                      style={{ width: 17, height: 17, accentColor: '#2D5016', cursor: 'pointer' }}/>
                    {sec.label}
                  </label>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setShowExportModal(false)} style={{ flex: 1, padding: '11px', background: '#F4F8EE', border: '2px solid #C8D9B0', borderRadius: 9, fontFamily: "'Nunito', sans-serif", fontWeight: 700, cursor: 'pointer', color: '#2D5016' }}>Cancel</button>
                <button onClick={runExport} style={{ flex: 2, padding: '11px', background: '#F5A623', border: 'none', borderRadius: 9, fontFamily: "'Nunito', sans-serif", fontWeight: 700, cursor: 'pointer', color: '#2D5016', fontSize: '0.95rem' }}>⬇ Download CSV</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Directory entry modal (add/edit) */}
      {showDirModal && (
        <DirectoryEntryModal
          entry={editDirEntry}
          onSave={saveDirectoryEntry}
          onClose={() => { setShowDirModal(false); setEditDirEntry(null); }}
        />
      )}

      {/* Add staff modal */}
      {showAddStaffModal && (
        <AddStaffModal onSave={addStaff} onClose={() => setShowAddStaffModal(false)} />
      )}

      {/* Delete staff confirmation */}
      {confirmDeleteStaff && (
        <div style={m.overlay} onClick={() => setConfirmDeleteStaff(null)}>
          <div style={{ ...m.modal, maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div style={m.modalHeader}>
              <h2 style={{ ...m.modalTitle, fontSize: '1.1rem' }}>Delete staff member?</h2>
              <button style={m.closeBtn} onClick={() => setConfirmDeleteStaff(null)}>✕</button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <p style={{ fontSize: '0.9rem', color: '#6B7F5A', marginBottom: 20 }}>
                This will permanently remove <strong>{confirmDeleteStaff}</strong> from the staff list and public directory. This cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setConfirmDeleteStaff(null)} style={{ flex: 1, padding: 11, background: '#F4F8EE', border: '2px solid #C8D9B0', borderRadius: 9, fontFamily: "'Nunito', sans-serif", fontWeight: 700, cursor: 'pointer', color: '#2D5016' }}>Cancel</button>
                <button onClick={() => deleteStaff(confirmDeleteStaff)} style={{ flex: 2, padding: 11, background: '#C0392B', border: 'none', borderRadius: 9, fontFamily: "'Nunito', sans-serif", fontWeight: 700, cursor: 'pointer', color: '#fff', fontSize: '0.95rem' }}>🗑 Yes, delete permanently</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle = { padding: '8px 10px', border: '2px solid #C8D9B0', borderRadius: 7, fontFamily: "'Nunito', sans-serif", fontSize: '0.88rem', outline: 'none', background: '#fff' };
const labelStyle = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.82rem', fontWeight: 700, color: '#2D5016' };
const DIR_CATEGORIES = ['Counselor', 'Program Staff', 'Day Camp Staff', 'Kitchen', 'Other'];

function DirectoryEntryModal({ entry, onSave, onClose }) {
  const isEdit = !!entry;
  const [form, setForm] = useState({
    email:      entry?.email      || '',
    name:       entry?.name       || '',
    role:       entry?.role       || '',
    category:   entry?.category   || 'Counselor',
    bio:        entry?.bio        || '',
    hometown:   entry?.hometown   || '',
    salt_years: entry?.salt_years ?? '',
    photo_url:  entry?.photo_url  || '',
    visible:    entry?.visible    ?? true,
    sort_order: entry?.sort_order ?? 0,
  });
  const [saving, setSaving] = useState(false);

  const handle = field => e =>
    setForm(f => ({ ...f, [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async () => {
    if (!form.email.trim() || !form.name.trim()) { alert('Email and name are required.'); return; }
    setSaving(true);
    const payload = { ...form, salt_years: form.salt_years === '' ? null : Number(form.salt_years) };
    const ok = await onSave(payload);
    if (!ok) setSaving(false);
  };

  const inp = { ...inputStyle, width: '100%', boxSizing: 'border-box' };

  return (
    <div style={m.overlay} onClick={onClose}>
      <div style={{ ...m.modal, maxWidth: 500 }} onClick={e => e.stopPropagation()}>
        <div style={m.modalHeader}>
          <h2 style={m.modalTitle}>{isEdit ? 'Edit Directory Entry' : 'Add Staff Manually'}</h2>
          <button style={m.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={{ ...m.body, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={labelStyle}>Name *<input style={inp} value={form.name} onChange={handle('name')} placeholder="Full name" /></label>
            <label style={labelStyle}>Email *<input style={{ ...inp, background: isEdit ? '#F4F8EE' : '#fff' }} value={form.email} onChange={handle('email')} placeholder="staff@email.com" disabled={isEdit} /></label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={labelStyle}>Position title<input style={inp} value={form.role} onChange={handle('role')} placeholder="e.g. Head Counselor" /></label>
            <label style={labelStyle}>Category
              <select style={inp} value={form.category} onChange={handle('category')}>
                {DIR_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={labelStyle}>Hometown<input style={inp} value={form.hometown} onChange={handle('hometown')} placeholder="City, State" /></label>
            <label style={labelStyle}>Years on SALT<input style={inp} type="number" min="0" value={form.salt_years} onChange={handle('salt_years')} placeholder="0" /></label>
          </div>
          <label style={labelStyle}>Bio
            <textarea style={{ ...inp, height: 80, resize: 'vertical' }} value={form.bio} onChange={handle('bio')} placeholder="A short bio…" />
          </label>
          <label style={labelStyle}>Photo URL<input style={inp} value={form.photo_url} onChange={handle('photo_url')} placeholder="https://…" /></label>
          <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: 10, cursor: 'pointer', fontWeight: 600 }}>
            <input type="checkbox" checked={form.visible} onChange={handle('visible')} style={{ width: 16, height: 16, accentColor: '#2D5016' }} />
            Show on public directory
          </label>
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button onClick={onClose} style={{ flex: 1, padding: 11, background: '#F4F8EE', border: '2px solid #C8D9B0', borderRadius: 9, fontFamily: "'Nunito', sans-serif", fontWeight: 700, cursor: 'pointer', color: '#2D5016' }}>Cancel</button>
            <button onClick={submit} disabled={saving} style={{ flex: 2, padding: 11, background: '#2D5016', border: 'none', borderRadius: 9, fontFamily: "'Nunito', sans-serif", fontWeight: 700, cursor: 'pointer', color: '#fff', fontSize: '0.95rem' }}>
              {saving ? 'Saving…' : isEdit ? '✓ Save changes' : '+ Add to directory'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddStaffModal({ onSave, onClose }) {
  const [email, setEmail]           = useState('');
  const [firstName, setFirstName]   = useState('');
  const [lastName, setLastName]     = useState('');
  const [isYearRound, setIsYearRound] = useState(false);
  const [saving, setSaving]         = useState(false);
  const inp = { ...inputStyle, width: '100%', boxSizing: 'border-box' };

  const submit = async () => {
    if (!email.trim()) { alert('Email is required.'); return; }
    setSaving(true);
    const ok = await onSave({ email, firstName, lastName, isYearRound });
    if (!ok) setSaving(false);
  };

  return (
    <div style={m.overlay} onClick={onClose}>
      <div style={{ ...m.modal, maxWidth: 400 }} onClick={e => e.stopPropagation()}>
        <div style={m.modalHeader}>
          <h2 style={m.modalTitle}>Add Staff Member</h2>
          <button style={m.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={{ ...m.body, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: '0.88rem', color: '#6B7F5A', margin: 0 }}>
            Creates a new staff record. The staff member can log in with this email to complete their onboarding.
          </p>
          <label style={labelStyle}>Email *<input style={inp} value={email} onChange={e => setEmail(e.target.value)} placeholder="name@email.com" /></label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={labelStyle}>First Name<input style={inp} value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First" /></label>
            <label style={labelStyle}>Last Name<input style={inp} value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last" /></label>
          </div>

          {/* Year Round / Admin toggle */}
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '10px 12px', borderRadius: 9, border: `2px solid ${isYearRound ? '#7BA05B' : '#C8D9B0'}`, background: isYearRound ? '#EAF4E2' : '#F4F8EE', transition: 'all 0.15s' }}>
            <input
              type="checkbox"
              checked={isYearRound}
              onChange={e => setIsYearRound(e.target.checked)}
              style={{ marginTop: 2, accentColor: '#2D5016', width: 16, height: 16, flexShrink: 0, cursor: 'pointer' }}
            />
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#2D5016' }}>Year Round / Admin</div>
              <div style={{ fontSize: '0.8rem', color: '#6B7F5A', marginTop: 2, lineHeight: 1.4 }}>
                Grants admin dashboard access. Marks them as Complete — no onboarding forms required.
              </div>
            </div>
          </label>

          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button onClick={onClose} style={{ flex: 1, padding: 11, background: '#F4F8EE', border: '2px solid #C8D9B0', borderRadius: 9, fontFamily: "'Nunito', sans-serif", fontWeight: 700, cursor: 'pointer', color: '#2D5016' }}>Cancel</button>
            <button onClick={submit} disabled={saving} style={{ flex: 2, padding: 11, background: '#2D5016', border: 'none', borderRadius: 9, fontFamily: "'Nunito', sans-serif", fontWeight: 700, cursor: 'pointer', color: '#fff', fontSize: '0.95rem' }}>
              {saving ? 'Adding…' : '+ Add staff member'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const ADMIN_CROP_FRAME = 240;

function DetailModal({ row, dirRecord, onClose, onRoleChange, onStatusOverride, onDelete, onPhotoUpdate, onUpdate }) {
  const p = row.personal_info || {};
  const f = row.forms || {};
  const a = row.availability || {};
  const o = row.optional_info || {};
  const [fileUrls, setFileUrls] = useState({});
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [localRole, setLocalRole] = useState(row.salt_role || '');
  const modalRef = React.useRef(null);

  // ── Edit mode state
  const [editMode, setEditMode]       = useState(false);
  const [savingEdits, setSavingEdits] = useState(false);
  const [localData, setLocalData]     = useState({
    personal:     row.personal_info || {},
    availability: row.availability  || {},
    optional:     row.optional_info || {},
  });
  const setLP = (field, val) => setLocalData(d => ({ ...d, personal:     { ...d.personal,     [field]: val } }));
  const setLA = (field, val) => setLocalData(d => ({ ...d, availability: { ...d.availability, [field]: val } }));
  const setLO = (field, val) => setLocalData(d => ({ ...d, optional:     { ...d.optional,     [field]: val } }));

  const saveEdits = async () => {
    setSavingEdits(true);
    const updates = {
      personal_info: localData.personal,
      availability:  localData.availability,
      optional_info: localData.optional,
    };
    const { error } = await supabase.from('onboarding_submissions').update(updates).eq('email', row.email);
    if (error) { alert('Save failed: ' + error.message); setSavingEdits(false); return; }
    if (onUpdate) onUpdate(updates);
    setEditMode(false);
    setSavingEdits(false);
  };

  // ── Photo crop state
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState(dirRecord?.photo_url || null);
  const [cropSrc,      setCropSrc]      = useState(null);
  const [cropOffset,   setCropOffset]   = useState({ x: 0, y: 0 });
  const [cropScale,    setCropScale]    = useState(1);
  const [cropMinScale, setCropMinScale] = useState(1);
  const [cropNatural,  setCropNatural]  = useState({ w: 0, h: 0 });
  const [photoUploading, setPhotoUploading] = useState(false);
  const [adminUploading, setAdminUploading] = useState({});
  const [localForms, setLocalForms] = useState(row.forms || {});
  const cropImgRef    = useRef(null);
  const photoInputRef = useRef(null);
  const adminFileInputRef = useRef(null);
  const pendingKeyRef = useRef(null);

  const uploadAdminFile = async (key, file) => {
    setAdminUploading(prev => ({ ...prev, [key]: true }));
    const safeName = file.name.replace(/\s+/g, '_');
    const emailSlug = row.email.replace(/[^a-z0-9]/gi, '-').toLowerCase();
    const path = `admin-uploads/${emailSlug}/${key}_${Date.now()}_${safeName}`;
    const { error: upErr } = await supabase.storage
      .from('onboarding-files')
      .upload(path, file, { upsert: true });
    if (upErr) {
      alert('Upload failed: ' + upErr.message);
      setAdminUploading(prev => ({ ...prev, [key]: false }));
      return;
    }
    const newForms = { ...localForms, [key]: path };
    const { error: dbErr } = await supabase
      .from('onboarding_submissions')
      .update({ forms: newForms })
      .eq('email', row.email);
    if (dbErr) {
      alert('DB update failed: ' + dbErr.message);
      setAdminUploading(prev => ({ ...prev, [key]: false }));
      return;
    }
    setLocalForms(newForms);
    const { data: urlData } = await supabase.storage
      .from('onboarding-files')
      .createSignedUrl(path, 3600);
    if (urlData) setFileUrls(prev => ({ ...prev, [key]: urlData.signedUrl }));
    setAdminUploading(prev => ({ ...prev, [key]: false }));
  };

  const cropExisting = () => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const scale = ADMIN_CROP_FRAME / Math.min(img.naturalWidth, img.naturalHeight);
      setCropNatural({ w: img.naturalWidth, h: img.naturalHeight });
      setCropMinScale(scale);
      setCropScale(scale);
      setCropOffset({ x: (ADMIN_CROP_FRAME - img.naturalWidth * scale) / 2, y: (ADMIN_CROP_FRAME - img.naturalHeight * scale) / 2 });
      setCropSrc(currentPhotoUrl);
    };
    img.src = currentPhotoUrl;
  };

  const handlePhotoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const scale = ADMIN_CROP_FRAME / Math.min(img.naturalWidth, img.naturalHeight);
        setCropNatural({ w: img.naturalWidth, h: img.naturalHeight });
        setCropMinScale(scale);
        setCropScale(scale);
        setCropOffset({ x: (ADMIN_CROP_FRAME - img.naturalWidth * scale) / 2, y: (ADMIN_CROP_FRAME - img.naturalHeight * scale) / 2 });
        setCropSrc(ev.target.result);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    // reset so the same file can be re-selected
    e.target.value = '';
  };

  const confirmCrop = () => {
    const img = cropImgRef.current;
    if (!img) return;
    const OUT = 600;
    const canvas = document.createElement('canvas');
    canvas.width = OUT; canvas.height = OUT;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, OUT, OUT);
    const sx = -cropOffset.x / cropScale;
    const sy = -cropOffset.y / cropScale;
    const sw = ADMIN_CROP_FRAME / cropScale;
    const sh = ADMIN_CROP_FRAME / cropScale;
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, OUT, OUT);
    canvas.toBlob(async (blob) => {
      setCropSrc(null);
      setPhotoUploading(true);
      const safeName = (row.id || row.email.replace(/[^a-z0-9]/gi, '_'));
      const path = `${safeName}_${Date.now()}.jpg`;
      const { error } = await supabase.storage.from('staff-photos').upload(path, blob, { upsert: true });
      if (!error) {
        const { data: { publicUrl } } = supabase.storage.from('staff-photos').getPublicUrl(path);
        await supabase.from('staff_directory').update({ photo_url: publicUrl }).eq('email', row.email);
        setCurrentPhotoUrl(publicUrl);
        if (onPhotoUpdate) onPhotoUpdate(publicUrl);
      }
      setPhotoUploading(false);
    }, 'image/jpeg', 0.92);
  };

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    // Focus the modal on open
    if (modalRef.current) modalRef.current.focus();
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    const loadUrls = async () => {
      setLoadingFiles(true);
      const keys = ['w4','i9','i9_listA','i9_listB','i9_listC','medical','directdeposit','cpr','lifeguard'];
      const urls = {};
      for (const k of keys) {
        if (f[k]) {
          const { data, error } = await supabase.storage
            .from('onboarding-files')
            .createSignedUrl(f[k], 3600);
          if (data && !error) urls[k] = data.signedUrl;
        }
      }
      if (!cancelled) { setFileUrls(urls); setLoadingFiles(false); }
    };
    loadUrls();
    return () => { cancelled = true; };
  }, [f]);

  const fileLabels = { w4: 'W-4', i9: 'I-9', i9_listA: 'I-9 List A Document', i9_listB: 'I-9 List B Document', i9_listC: 'I-9 List C Document', medical: 'Health Form', directdeposit: 'Direct Deposit Form', cpr: 'CPR / First Aid Cert', lifeguard: 'Lifeguard Cert' };

  return (
    <div style={m.overlay} onClick={onClose} role="dialog" aria-modal="true" aria-label={`Details for ${p.firstName || ''} ${p.lastName || ''}`}>
      <div style={m.modal} onClick={e => e.stopPropagation()} ref={modalRef} tabIndex={-1}>
        <div style={m.modalHeader}>
          <h2 style={m.modalTitle}>{localData.personal.firstName || p.firstName} {localData.personal.lastName || p.lastName}</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {!editMode ? (
              <button onClick={() => setEditMode(true)}
                style={{ padding: '6px 14px', background: '#F5A623', color: '#2D5016', border: 'none', borderRadius: 7, fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
                ✏️ Edit Info
              </button>
            ) : (
              <>
                <button onClick={() => { setLocalData({ personal: row.personal_info || {}, availability: row.availability || {}, optional: row.optional_info || {} }); setEditMode(false); }}
                  style={{ padding: '6px 14px', background: '#F4F8EE', color: '#2D5016', border: '1.5px solid #C8D9B0', borderRadius: 7, fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={saveEdits} disabled={savingEdits}
                  style={{ padding: '6px 14px', background: '#2D5016', color: '#fff', border: 'none', borderRadius: 7, fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
                  {savingEdits ? 'Saving…' : '✓ Save changes'}
                </button>
              </>
            )}
            <button style={m.closeBtn} onClick={onClose} aria-label="Close detail modal">✕</button>
          </div>
        </div>
        <div style={m.body}>
          {/* Hidden admin file input — shared across all form keys */}
          <input
            ref={adminFileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={e => {
              const file = e.target.files?.[0];
              const key = pendingKeyRef.current;
              if (file && key) uploadAdminFile(key, file);
              e.target.value = '';
            }}
          />

          {/* ── Photo / crop section ── */}
          <MSection title="Profile Photo">
            <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoFile} />
            {!cropSrc ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 80, height: 80, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: '#C8D9B0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {currentPhotoUrl
                    ? <img src={currentPhotoUrl} alt="profile" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }} />
                    : <span style={{ fontSize: '2rem' }}>👤</span>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {currentPhotoUrl && (
                    <button
                      onClick={cropExisting}
                      style={{ padding: '8px 14px', background: '#EAF4E2', border: '2px solid #7BA05B', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontWeight: 700, cursor: 'pointer', color: '#2D5016', fontSize: '0.88rem' }}>
                      ✂️ Adjust / Crop Existing
                    </button>
                  )}
                  <button
                    onClick={() => photoInputRef.current?.click()}
                    disabled={photoUploading}
                    style={{ padding: '8px 14px', background: '#F4F8EE', border: '2px solid #C8D9B0', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontWeight: 700, cursor: 'pointer', color: '#2D5016', fontSize: '0.88rem' }}>
                    {photoUploading ? '⏳ Uploading…' : currentPhotoUrl ? '🔄 Replace with New Photo' : '📷 Upload Photo'}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {/* Crop frame */}
                <div
                  style={{ position: 'relative', width: ADMIN_CROP_FRAME, height: ADMIN_CROP_FRAME, margin: '0 auto 12px', overflow: 'hidden', cursor: 'grab', userSelect: 'none', background: '#111', touchAction: 'none', borderRadius: 8 }}
                  onMouseDown={e => {
                    e.preventDefault();
                    const startX = e.clientX - cropOffset.x, startY = e.clientY - cropOffset.y;
                    const iw = cropNatural.w * cropScale, ih = cropNatural.h * cropScale;
                    const clamp = (nx, ny) => ({ x: Math.min(0, Math.max(ADMIN_CROP_FRAME - iw, nx)), y: Math.min(0, Math.max(ADMIN_CROP_FRAME - ih, ny)) });
                    const onMove = me => setCropOffset(clamp(me.clientX - startX, me.clientY - startY));
                    const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
                    window.addEventListener('mousemove', onMove);
                    window.addEventListener('mouseup', onUp);
                  }}
                  onTouchStart={e => {
                    const t = e.touches[0];
                    const startX = t.clientX - cropOffset.x, startY = t.clientY - cropOffset.y;
                    const iw = cropNatural.w * cropScale, ih = cropNatural.h * cropScale;
                    const clamp = (nx, ny) => ({ x: Math.min(0, Math.max(ADMIN_CROP_FRAME - iw, nx)), y: Math.min(0, Math.max(ADMIN_CROP_FRAME - ih, ny)) });
                    const onMove = te => { const tt = te.touches[0]; setCropOffset(clamp(tt.clientX - startX, tt.clientY - startY)); };
                    const onUp = () => { window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onUp); };
                    window.addEventListener('touchmove', onMove, { passive: true });
                    window.addEventListener('touchend', onUp);
                  }}
                >
                  <img ref={cropImgRef} src={cropSrc} crossOrigin="anonymous" alt="crop preview" draggable={false}
                    style={{ position: 'absolute', left: cropOffset.x, top: cropOffset.y, width: cropNatural.w * cropScale, height: cropNatural.h * cropScale, pointerEvents: 'none', userSelect: 'none', display: 'block' }}
                  />
                  <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                    <defs>
                      <mask id="adminCropHole">
                        <rect width="100%" height="100%" fill="white" />
                        <circle cx={ADMIN_CROP_FRAME / 2} cy={ADMIN_CROP_FRAME / 2} r={ADMIN_CROP_FRAME / 2 - 2} fill="black" />
                      </mask>
                    </defs>
                    <rect width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#adminCropHole)" />
                    <circle cx={ADMIN_CROP_FRAME / 2} cy={ADMIN_CROP_FRAME / 2} r={ADMIN_CROP_FRAME / 2 - 2} fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" strokeDasharray="6 4" />
                  </svg>
                </div>
                {/* Scale slider */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span style={{ fontSize: '0.8rem', color: '#6B7F5A' }}>🔍</span>
                  <input type="range" min={cropMinScale} max={cropMinScale * 4} step={cropMinScale * 0.01} value={cropScale}
                    onChange={e => setCropScale(parseFloat(e.target.value))}
                    style={{ flex: 1, accentColor: '#2D5016' }} />
                </div>
                {/* Actions */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setCropSrc(null)}
                    style={{ flex: 1, padding: '9px', background: 'none', border: '2px solid #C8D9B0', borderRadius: 9, fontFamily: "'Nunito', sans-serif", fontWeight: 700, cursor: 'pointer', color: '#6B7F5A', fontSize: '0.88rem' }}>
                    Cancel
                  </button>
                  <button onClick={confirmCrop}
                    style={{ flex: 2, padding: '9px', background: '#2D5016', border: 'none', borderRadius: 9, color: '#fff', fontWeight: 700, fontFamily: "'Nunito', sans-serif", cursor: 'pointer', fontSize: '0.88rem' }}>
                    ✓ Use photo
                  </button>
                </div>
              </div>
            )}
          </MSection>

          <MSection title="SALT Role">
            <select
              value={localRole}
              onChange={e => {
                setLocalRole(e.target.value);
                onRoleChange(e.target.value);
              }}
              disabled={!row.id || row._notStarted}
              style={{ fontSize: '0.9rem', padding: '8px 12px', borderRadius: 8, border: '2px solid #C8D9B0', background: localRole ? '#EAF4E2' : '#F4F8EE', color: '#2D5016', fontFamily: "'Nunito', sans-serif", fontWeight: 700, cursor: 'pointer', width: '100%' }}
            >
              <option value="">— Assign role —</option>
              {SALT_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </MSection>
          <MSection title="Status Override">
            <select
              value={row.checks?.statusOverride || ''}
              onChange={e => onStatusOverride(e.target.value)}
              disabled={!row.id || row._notStarted}
              style={{ fontSize: '0.9rem', padding: '8px 12px', borderRadius: 8, border: '2px solid #C8D9B0', background: row.checks?.statusOverride ? '#EAF4E2' : '#F4F8EE', color: '#2D5016', fontFamily: "'Nunito', sans-serif", fontWeight: 700, cursor: 'pointer', width: '100%' }}
            >
              <option value="">Auto (computed from form data)</option>
              <option value="complete">✅ Mark as Complete</option>
              <option value="in_progress">🕐 Mark as In Progress</option>
              <option value="not_started">— Mark as Not Started</option>
            </select>
            {row.checks?.statusOverride && (
              <p style={{ fontSize: '0.78rem', color: '#7a4e00', marginTop: 6, margin: '6px 0 0' }}>
                ⚠ Status is manually overridden. Select "Auto" to revert to computed status.
              </p>
            )}
          </MSection>
          <MSection title="Contact">
            {editMode ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <AField label="First Name"   value={localData.personal.firstName}   onChange={v => setLP('firstName', v)} />
                <AField label="Last Name"    value={localData.personal.lastName}    onChange={v => setLP('lastName', v)} />
                <AField label="Phone"        value={localData.personal.phone}       onChange={v => setLP('phone', v)} />
                <AField label="Date of Birth" value={localData.personal.dob}        onChange={v => setLP('dob', v)} type="date" />
                <AField label="Street"       value={localData.personal.address}     onChange={v => setLP('address', v)} span />
                <AField label="City"         value={localData.personal.city}        onChange={v => setLP('city', v)} />
                <AField label="State"        value={localData.personal.state}       onChange={v => setLP('state', v)}
                  options={['','WI','IL','MN','MI','IA','Other']} />
                <AField label="ZIP"          value={localData.personal.zip}         onChange={v => setLP('zip', v)} />
              </div>
            ) : (
              <>
                <MRow label="Email"   value={row.email} />
                <MRow label="Phone"   value={formatPhone(localData.personal.phone)} />
                <MRow label="DOB"     value={localData.personal.dob} />
                <MRow label="Address" value={[localData.personal.address, localData.personal.city, localData.personal.state, localData.personal.zip].filter(Boolean).join(', ')} />
              </>
            )}
          </MSection>
          <MSection title="Emergency Contact">
            {editMode ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <AField label="Name"         value={localData.personal.emergencyName}         onChange={v => setLP('emergencyName', v)} />
                <AField label="Relationship" value={localData.personal.emergencyRelationship} onChange={v => setLP('emergencyRelationship', v)} />
                <AField label="Phone"        value={localData.personal.emergencyPhone}        onChange={v => setLP('emergencyPhone', v)} span />
              </div>
            ) : (
              <>
                <MRow label="Name"         value={localData.personal.emergencyName} />
                <MRow label="Relationship" value={localData.personal.emergencyRelationship} />
                <MRow label="Phone"        value={formatPhone(localData.personal.emergencyPhone)} />
              </>
            )}
          </MSection>
          <MSection title="Medical">
            {editMode ? (
              <>
                <AField label="Allergies"  value={localData.personal.allergies}   onChange={v => setLP('allergies', v)}   textarea />
                <AField label="Medications" value={localData.personal.medications} onChange={v => setLP('medications', v)} textarea />
                <AField label="Notes"      value={localData.personal.medicalNotes} onChange={v => setLP('medicalNotes', v)} textarea />
              </>
            ) : (
              <>
                <MRow label="Allergies"   value={localData.personal.allergies} />
                <MRow label="Medications" value={localData.personal.medications} />
                <MRow label="Notes"       value={localData.personal.medicalNotes} />
              </>
            )}
          </MSection>
          <MSection title="Payment">
            <MRow label="SSN"     value={p.ssn ? `●●●-●●-${p.ssn.slice(-4)}` : '—'} />
            <MRow label="Bank"    value={p.bankName} />
            <MRow label="Routing" value={p.routing} />
            <MRow label="Account" value={p.accountNumber} />
          </MSection>
          <MSection title="T-Shirts">
            {editMode ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {SIZES.map(sz => {
                  const qty = localData.personal.shirts?.[sz] || 0;
                  return (
                    <div key={sz} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: '#F4F8EE', borderRadius: 8, border: '1px solid #C8D9B0' }}>
                      <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#2D5016', width: 40 }}>Size {sz}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <button style={s.qtyBtn} onClick={() => setLP('shirts', { ...localData.personal.shirts, [sz]: Math.max(0, qty - 1) })}>−</button>
                        <span style={{ fontWeight: 700, width: 20, textAlign: 'center' }}>{qty}</span>
                        <button style={s.qtyBtn} onClick={() => setLP('shirts', { ...localData.personal.shirts, [sz]: qty + 1 })}>+</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                  {SIZES.map(sz => {
                    const qty = localData.personal.shirts?.[sz] || 0;
                    return qty > 0 ? (
                      <span key={sz} style={{ background: '#2D5016', color: '#fff', padding: '4px 12px', borderRadius: 20, fontSize: '0.82rem', fontWeight: 700 }}>{sz} × {qty}</span>
                    ) : null;
                  })}
                  {!Object.values(localData.personal.shirts || {}).some(v => v > 0) && <span style={{ color: '#6B7F5A', fontSize: '0.88rem' }}>None ordered</span>}
                </div>
                <MRow label="Extra cost" value={`$${Math.max(0, Object.values(localData.personal.shirts || {}).reduce((a,b)=>a+b,0) - 1) * 8}`} />
              </>
            )}
          </MSection>
          <MSection title="Uploaded Forms">
            {loadingFiles ? (
              <p style={{ fontSize: '0.85rem', color: '#6B7F5A' }}>Loading file links…</p>
            ) : (
              ['w4','i9','i9_listA','i9_listB','i9_listC','medical','directdeposit','cpr','lifeguard'].map(k => {
                const hasFile = !!localForms[k];
                const isUploading = !!adminUploading[k];
                return (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, padding: '8px 12px', background: hasFile ? '#EAF4E2' : '#F4F8EE', borderRadius: 8, border: `1px solid ${hasFile ? '#7BA05B' : '#C8D9B0'}`, gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#2D5016', minWidth: 140 }}>{fileLabels[k]}</span>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      {fileUrls[k] && (
                        <>
                          <a href={fileUrls[k]} target="_blank" rel="noreferrer"
                            style={{ padding: '5px 10px', background: '#2E7DAF', color: '#fff', borderRadius: 6, fontSize: '0.8rem', fontWeight: 700, textDecoration: 'none' }}>
                            View ↗
                          </a>
                          <a href={fileUrls[k]} download
                            style={{ padding: '5px 10px', background: '#2D5016', color: '#fff', borderRadius: 6, fontSize: '0.8rem', fontWeight: 700, textDecoration: 'none' }}>
                            ⬇
                          </a>
                        </>
                      )}
                      {!fileUrls[k] && hasFile && (
                        <span style={{ fontSize: '0.78rem', color: '#e74c3c' }}>⚠ URL error</span>
                      )}
                      {!hasFile && !isUploading && (
                        <span style={{ fontSize: '0.78rem', color: '#6B7F5A' }}>Not uploaded</span>
                      )}
                      <button
                        disabled={isUploading}
                        onClick={() => { pendingKeyRef.current = k; adminFileInputRef.current?.click(); }}
                        style={{ padding: '5px 10px', background: isUploading ? '#C8D9B0' : hasFile ? '#fff' : '#F5A623', border: `1.5px solid ${hasFile ? '#C8D9B0' : '#F5A623'}`, borderRadius: 6, fontSize: '0.78rem', fontWeight: 700, cursor: isUploading ? 'default' : 'pointer', color: hasFile ? '#6B7F5A' : '#2D5016', fontFamily: "'Nunito', sans-serif" }}>
                        {isUploading ? '⏳ Uploading…' : hasFile ? '↺ Replace' : '⬆ Upload'}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
            <MRow label="Background check" value={localForms.bgCheckDone ? '✅ Completed' : 'Not done'} />
          </MSection>
          <MSection title="Availability">
            {editMode ? (
              <>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: '0.78rem', color: '#6B7F5A', marginBottom: 5, fontWeight: 700 }}>Ropes Training</div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    {[['Yes', true], ['No', false]].map(([lbl, val]) => (
                      <button key={lbl} onClick={() => setLA('ropes', val)}
                        style={{ flex: 1, padding: '7px', border: `2px solid ${localData.availability.ropes === val ? '#2D5016' : '#C8D9B0'}`, background: localData.availability.ropes === val ? '#EAF4E2' : '#fff', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontWeight: 700, cursor: 'pointer', color: '#2D5016', fontSize: '0.85rem' }}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                  {localData.availability.ropes === true && (
                    <AField label="Level" value={localData.availability.ropesLevel} onChange={v => setLA('ropesLevel', v)}
                      options={['', 'Level 1 & 2 Trainee', 'Level 3 Trainee', 'Instructor', 'Other']} />
                  )}
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: '0.78rem', color: '#6B7F5A', marginBottom: 5, fontWeight: 700 }}>Fancy Schmancy</div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    {[['Yes', true], ['No', false]].map(([lbl, val]) => (
                      <button key={lbl} onClick={() => setLA('fancy', val)}
                        style={{ flex: 1, padding: '7px', border: `2px solid ${localData.availability.fancy === val ? '#2D5016' : '#C8D9B0'}`, background: localData.availability.fancy === val ? '#EAF4E2' : '#fff', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontWeight: 700, cursor: 'pointer', color: '#2D5016', fontSize: '0.85rem' }}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                  {localData.availability.fancy === true && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {FANCY_ROLES.map(role => {
                        const active = (localData.availability.fancyRoles || []).includes(role);
                        return (
                          <button key={role} onClick={() => setLA('fancyRoles', active ? (localData.availability.fancyRoles || []).filter(r => r !== role) : [...(localData.availability.fancyRoles || []), role])}
                            style={{ padding: '5px 12px', border: `2px solid ${active ? '#2D5016' : '#C8D9B0'}`, background: active ? '#2D5016' : '#fff', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', color: active ? '#fff' : '#2D5016', fontFamily: "'Nunito', sans-serif" }}>
                            {role}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: '0.78rem', color: '#6B7F5A', marginBottom: 5, fontWeight: 700 }}>Junior Staff Training</div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    {[['Yes', 'yes'], ['Partial', 'partial'], ['No', 'no']].map(([lbl, val]) => (
                      <button key={lbl} onClick={() => setLA('junior', val)}
                        style={{ flex: 1, padding: '7px', border: `2px solid ${localData.availability.junior === val ? '#2D5016' : '#C8D9B0'}`, background: localData.availability.junior === val ? '#EAF4E2' : '#fff', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontWeight: 700, cursor: 'pointer', color: '#2D5016', fontSize: '0.82rem' }}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                  {localData.availability.junior === 'partial' && (
                    <AField label="Unavailable dates" value={localData.availability.juniorDates} onChange={v => setLA('juniorDates', v)} textarea />
                  )}
                </div>
              </>
            ) : (
              <>
                <MRow label="Ropes Training" value={localData.availability.ropes===true ? `Yes — ${localData.availability.ropesLevel||''} ${localData.availability.ropesOtherDetail||''}`.trim() : localData.availability.ropes===false ? 'No' : '—'} />
                <MRow label="Fancy Schmancy" value={localData.availability.fancy===true ? `Yes — ${(localData.availability.fancyRoles||[]).join(', ')||'no roles'}` : localData.availability.fancy===false ? 'No' : '—'} />
                <MRow label="Junior Staff"   value={localData.availability.junior||'—'} />
                {localData.availability.juniorDates && <MRow label="Unavailable dates" value={localData.availability.juniorDates} />}
              </>
            )}
          </MSection>
          <MSection title="Optional">
            {editMode ? (
              <>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: '0.78rem', color: '#6B7F5A', marginBottom: 5, fontWeight: 700 }}>Muggers ☕</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[['Yes ☕', 'yes'], ['No', 'no']].map(([lbl, val]) => (
                      <button key={lbl} onClick={() => setLO('muggers', val)}
                        style={{ flex: 1, padding: '7px', border: `2px solid ${localData.optional.muggers === val ? '#2D5016' : '#C8D9B0'}`, background: localData.optional.muggers === val ? '#EAF4E2' : '#fff', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontWeight: 700, cursor: 'pointer', color: '#2D5016', fontSize: '0.85rem' }}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.78rem', color: '#6B7F5A', marginBottom: 5, fontWeight: 700 }}>Music 🎵</div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    {[['Yes', 'yes'], ['No', 'no']].map(([lbl, val]) => (
                      <button key={lbl} onClick={() => setLO('musicInterest', val)}
                        style={{ flex: 1, padding: '7px', border: `2px solid ${localData.optional.musicInterest === val ? '#2D5016' : '#C8D9B0'}`, background: localData.optional.musicInterest === val ? '#EAF4E2' : '#fff', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontWeight: 700, cursor: 'pointer', color: '#2D5016', fontSize: '0.85rem' }}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                  {localData.optional.musicInterest === 'yes' && (
                    <>
                      <AField label="Instruments / notes"  value={localData.optional.musicInstruments}      onChange={v => setLO('musicInstruments', v)} textarea />
                      <AField label="Sundays unavailable"  value={localData.optional.musicDatesUnavailable} onChange={v => setLO('musicDatesUnavailable', v)} textarea />
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <MRow label="Muggers" value={localData.optional.muggers==='yes' ? 'Joined ☕' : localData.optional.muggers==='no' ? 'No' : '—'} />
                <MRow label="Music"   value={localData.optional.musicInterest==='yes' ? `Yes — ${localData.optional.musicInstruments||''}` : localData.optional.musicInterest==='no' ? 'No' : '—'} />
                {localData.optional.musicDatesUnavailable && <MRow label="Music unavailable" value={localData.optional.musicDatesUnavailable} />}
              </>
            )}
          </MSection>
          <MSection title="Submission">
            <MRow label="Status" value={row.submitted_at ? '✅ Submitted' : '⏳ In Progress'} />
            <MRow label="Submitted" value={row.submitted_at ? new Date(row.submitted_at).toLocaleString() : '—'} />
            <MRow label="Last updated" value={row.updated_at ? new Date(row.updated_at).toLocaleString() : '—'} />
          </MSection>
          {onDelete && (
            <div style={{ borderTop: '1px solid #F0F4EA', paddingTop: 16, marginTop: 4 }}>
              <button onClick={onDelete}
                style={{ width: '100%', padding: '10px', background: 'none', border: '1.5px solid #f7c1c1', borderRadius: 9, fontFamily: "'Nunito', sans-serif", fontWeight: 700, cursor: 'pointer', color: '#c0392b', fontSize: '0.88rem' }}>
                🗑 Delete staff member…
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MSection({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontWeight: 700, fontSize: '0.78rem', letterSpacing: 1, textTransform: 'uppercase', color: '#7BA05B', marginBottom: 8, borderBottom: '1px solid #C8D9B0', paddingBottom: 4 }}>{title}</div>
      {children}
    </div>
  );
}
function MRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 5, fontSize: '0.88rem' }}>
      <span style={{ color: '#6B7F5A', minWidth: 140, flexShrink: 0 }}>{label}</span>
      <span style={{ color: '#1C2B0E' }}>{value || '—'}</span>
    </div>
  );
}

// Admin edit field — used in DetailModal edit mode
function AField({ label, value, onChange, type = 'text', options, textarea, span }) {
  const base = { width: '100%', padding: '7px 10px', border: '2px solid #C8D9B0', borderRadius: 7, fontFamily: "'Nunito', sans-serif", fontSize: '0.88rem', color: '#1C2B0E', outline: 'none', boxSizing: 'border-box', background: '#fff' };
  return (
    <div style={{ marginBottom: 8, gridColumn: span ? '1 / -1' : undefined }}>
      <label style={{ display: 'block', fontSize: '0.75rem', color: '#6B7F5A', fontWeight: 700, marginBottom: 3 }}>{label}</label>
      {options ? (
        <select value={value || ''} onChange={e => onChange(e.target.value)} style={base}>
          {options.map(o => <option key={o} value={o}>{o || '—'}</option>)}
        </select>
      ) : textarea ? (
        <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={2}
          style={{ ...base, resize: 'vertical', minHeight: 56 }} />
      ) : (
        <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} style={base} />
      )}
    </div>
  );
}

const s = {
  wrap: { fontFamily: "'Nunito', sans-serif", backgroundImage: 'url(/camp_bg.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed', minHeight: '100vh', paddingBottom: 60 },
  header: { background: 'linear-gradient(135deg, #2D5016, #3E6B1F)', color: '#fff', padding: '24px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 },
  badge: { display: 'inline-block', background: '#F5A623', color: '#2D5016', fontWeight: 700, fontSize: '0.7rem', letterSpacing: 2, textTransform: 'uppercase', padding: '3px 12px', borderRadius: 20, marginBottom: 6 },
  title: { fontFamily: "'Playfair Display', serif", fontSize: '1.6rem', fontWeight: 900, margin: 0 },
  exportBtn: { padding: '9px 18px', background: '#F5A623', color: '#2D5016', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontFamily: "'Nunito', sans-serif", fontSize: '0.9rem' },
  signOutBtn: { padding: '9px 18px', background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontFamily: "'Nunito', sans-serif", fontSize: '0.9rem' },
  statsRow: { display: 'flex', gap: 12, padding: '20px 32px', flexWrap: 'wrap' },
  statCard: { background: '#fff', border: '1px solid #C8D9B0', borderRadius: 12, padding: '16px 24px', minWidth: 110, flex: 1 },
  statNum: { fontSize: '2rem', fontWeight: 700, color: '#2D5016' },
  statLabel: { fontSize: '0.8rem', color: '#6B7F5A', marginTop: 2 },
  tabRow: { display: 'flex', gap: 6, padding: '0 32px 16px', flexWrap: 'wrap' },
  tabBtn: { padding: '7px 14px', border: '2px solid #C8D9B0', background: '#fff', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', color: '#2D5016', fontFamily: "'Nunito', sans-serif" },
  tabActive: { background: '#2D5016', color: '#fff', borderColor: '#2D5016' },
  filterSelect: { padding: '8px 12px', border: '2px solid #C8D9B0', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontSize: '0.88rem', color: '#2D5016', background: '#fff', outline: 'none', cursor: 'pointer' },
  search: { display: 'block', margin: '0 32px 16px', padding: '9px 14px', border: '2px solid #C8D9B0', borderRadius: 8, width: 'calc(100% - 64px)', fontFamily: "'Nunito', sans-serif", fontSize: '0.95rem', boxSizing: 'border-box', outline: 'none' },
  tableWrap: { overflowX: 'auto', padding: '0 32px' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 8px rgba(45,80,22,0.07)', fontSize: '0.88rem' },
  th: { padding: '12px 14px', textAlign: 'left', fontWeight: 700, color: '#2D5016', background: '#F4F8EE', borderBottom: '2px solid #C8D9B0', whiteSpace: 'nowrap' },
  td: { padding: '12px 14px', borderBottom: '1px solid #F0F4EA', verticalAlign: 'middle' },
  tr: {},
  pill: { display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 700 },
  viewBtn: { padding: '5px 12px', background: '#F4F8EE', border: '1px solid #C8D9B0', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem', color: '#2D5016', fontFamily: "'Nunito', sans-serif" },
};
const m = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
  modal: { background: '#fff', borderRadius: 16, maxWidth: 600, width: '100%', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 16px', borderBottom: '2px solid #C8D9B0' },
  modalTitle: { fontFamily: "'Playfair Display', serif", color: '#2D5016', margin: 0, fontSize: '1.4rem' },
  closeBtn: { background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#6B7F5A', padding: 4 },
  body: { overflowY: 'auto', padding: '20px 24px' },
};